import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmt } from "@/lib/api";
import { RegisterTable, RegisterToolbar, FilterSelect, StatusTag } from "@/components/RegisterTable";
import { DetailDrawer } from "@/components/DetailDrawer";

const BAND_COLOR = {
  high_confidence: "var(--blocker)",
  medium_confidence: "var(--ink)",
  low_confidence: "var(--ink-mute)",
};

// ─── Phase B-1 · Plain-English signal explanations ─────────────────────────
// Maps each detection signal name to a controller-readable sentence.
// `value` is the raw signal value (string/number/bool/list) for substitution.
const SIGNAL_EXPLANATIONS = {
  exact_date_match: () => "Both transactions posted on the same date.",
  exact_amount_match: () => "Identical unsigned amount.",
  exact_reference_match: (v) => `Identical reference number (${v}).`,
  description_similar: (v) => `Descriptions are ~${Math.round((Number(v) || 0) * 100)}% similar.`,
  same_import_batch: () => "Both came from the SAME import batch — likely two real rows, not a duplicate (counts against the score).",
  different_import_batches: () => "Came from DIFFERENT import batches — typical fingerprint of a re-imported file.",
  same_polarity: (v) => `Both are ${v} (same direction).`,
  recent_reimport_window: (v) => `Source batches were uploaded within ${v} hours of each other.`,
  row_fingerprint_match: () => "Both rows hash to the identical fingerprint (date + amount + reference + normalized description).",
  polarity_mismatch: () => "Polarity differs — this is a reversal, not a duplicate (rules out the cluster).",
};

const explainSignal = (s) => {
  const fn = SIGNAL_EXPLANATIONS[s.name];
  return fn ? fn(s.value) : `${s.name}: ${s.value}`;
};

// ─── Phase B-2 · Disposition option help copy ──────────────────────────────
const DISPOSITION_HELP = {
  confirmed_duplicate:
    "The engine was correct. The transaction you mark as 'kept' stays under review; all others in the cluster are excluded as 'duplicate_confirmed' (reversible). Bank + GL reconciliation will refresh.",
  kept_both:
    "Acknowledge the engine but keep ALL transactions active. They return to 'under_review' (or 'classified' if already classified). No exclusion is written. Use when you intend to match each one separately.",
  valid_repeat_activity:
    "These are legitimate distinct events that happen to look similar (e.g. weekly identical payroll). Transactions return to 'under_review'. No exclusion is written.",
  false_positive:
    "The engine was wrong. The cluster is marked false_positive for audit, but the transactions are not touched at all.",
};

// ─── Phase B-4 · Outcome banner copy (post-disposition) ────────────────────
function outcomeMessage(cluster) {
  const n = (cluster.transactions || []).length;
  switch (cluster.status) {
    case "confirmed_duplicate":
      return `Confirmed duplicate · ${n - 1} transaction${n - 1 === 1 ? "" : "s"} excluded · 1 kept · reconciliation refreshed.`;
    case "kept_both":
      return `Kept both · all ${n} transactions returned to active lifecycle.`;
    case "valid_repeat_activity":
      return `Marked valid repeat activity · all ${n} transactions returned to active lifecycle.`;
    case "false_positive":
      return `Marked false positive · no transactions were modified.`;
    default:
      return null;
  }
}

export default function DuplicateRegister() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: null, confidence_band: null });
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = () => {
    setLoading(true);
    api.listDuplicateClusters({
      status: filters.status || undefined,
      confidence_band: filters.confidence_band || undefined,
    }).then(d => { setRows(d); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(load, [filters.status, filters.confidence_band]);

  // Phase B-3 · honor ?cluster=<id> deep-link from Workspace blocker card.
  useEffect(() => {
    const cid = searchParams.get("cluster");
    if (cid && cid !== selectedId) setSelectedId(cid);
  }, [searchParams, selectedId]);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    api.getDuplicateCluster(selectedId).then(setDetail);
  }, [selectedId]);

  const closeDrawer = () => {
    setSelectedId(null);
    // Strip the cluster query param so the drawer doesn't re-open on back/forward.
    if (searchParams.get("cluster")) {
      searchParams.delete("cluster");
      setSearchParams(searchParams, { replace: true });
    }
  };

  const columns = [
    { key: "id", label: "Cluster", width: "160px",
      render: r => <span className="mono" style={{ fontSize: 11 }}>{r.id.slice(0, 8)}…</span> },
    { key: "period_label", label: "Period", width: "120px" },
    { key: "account_name", label: "Account", width: "150px" },
    { key: "source", label: "Source", width: "70px" },
    { key: "n", label: "Items", width: "60px", align: "right",
      render: r => (r.transaction_ids || []).length },
    { key: "score", label: "Score", width: "70px", align: "right",
      render: r => <span className="mono">{(r.score || 0).toFixed(2)}</span> },
    { key: "confidence_band", label: "Confidence", width: "110px",
      render: r => (
        <span style={{
          color: BAND_COLOR[r.confidence_band] || "var(--ink)",
          fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em",
        }}>{(r.confidence_band || "").replace("_confidence", "")}</span>
      )},
    { key: "status", label: "Status", width: "130px",
      render: r => <StatusTag status={r.status} testid={`dup-status-${r.id}`} /> },
    { key: "created_at", label: "Detected", width: "110px",
      render: r => fmt.date(r.created_at) },
  ];

  return (
    <div data-testid="duplicate-register">
      <header style={{ marginBottom: 24 }}>
        <div className="heading h1">Duplicate Register</div>
        <div className="muted" style={{ marginTop: 4, fontSize: 14 }}>
          Cross-period view of duplicate clusters detected by the v2.0.0 rule engine (GOV-022).
        </div>
      </header>

      <RegisterToolbar testid="dup-toolbar">
        <FilterSelect
          label="Status"
          value={filters.status}
          onChange={v => setFilters(f => ({ ...f, status: v }))}
          options={[
            { value: "pending_review", label: "Pending Review" },
            { value: "under_review", label: "Under Review" },
            { value: "confirmed_duplicate", label: "Confirmed Duplicate" },
            { value: "kept_both", label: "Kept Both" },
            { value: "valid_repeat_activity", label: "Valid Repeat Activity" },
            { value: "false_positive", label: "False Positive" },
          ]}
          testid="dup-filter-status"
        />
        <FilterSelect
          label="Confidence"
          value={filters.confidence_band}
          onChange={v => setFilters(f => ({ ...f, confidence_band: v }))}
          options={[
            { value: "high_confidence", label: "High" },
            { value: "medium_confidence", label: "Medium" },
            { value: "low_confidence", label: "Low" },
          ]}
          testid="dup-filter-confidence"
        />
        <div style={{ flex: 1 }} />
        <div className="muted" style={{ fontSize: 12 }} data-testid="dup-count">
          {loading ? "Loading…" : `${rows.length} cluster${rows.length === 1 ? "" : "s"}`}
        </div>
      </RegisterToolbar>

      <RegisterTable
        columns={columns}
        rows={rows}
        onRowClick={(r) => setSelectedId(r.id)}
        emptyText={loading ? "Loading…" : "No duplicate clusters match these filters."}
        testid="dup-register-table"
      />

      <DetailDrawer
        open={!!selectedId}
        onClose={closeDrawer}
        title="Duplicate Cluster"
        subtitle={detail ? `${detail.period_label} · ${detail.account_name} · ${detail.source.toUpperCase()}` : ""}
        testid="dup-detail"
      >
        {detail && <ClusterDetail cluster={detail} onActionComplete={() => { load(); api.getDuplicateCluster(selectedId).then(setDetail); }} />}
      </DetailDrawer>
    </div>
  );
}

function ClusterDetail({ cluster, onActionComplete }) {
  const [disposition, setDisposition] = useState("");
  const [notes, setNotes] = useState("");
  const [keptId, setKeptId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const pending = cluster.status === "pending_review" || cluster.status === "under_review";
  const outcomeText = !pending ? outcomeMessage(cluster) : null;

  async function dispose() {
    if (!disposition) { setError("Disposition is required."); return; }
    if (!notes.trim()) { setError("Controller notes are required."); return; }
    if (disposition === "confirmed_duplicate" && !keptId) {
      setError("Select which transaction to keep when confirming the duplicate.");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await api.disposeCluster(cluster.id, {
        disposition, controller_notes: notes,
        kept_transaction_id: keptId || null,
      });
      onActionComplete();
    } catch (e) {
      setError(e?.response?.data?.detail || "Disposition failed.");
    } finally {
      setBusy(false);
    }
  }

  // Phase B-1 · Build human-readable bullets (skip negative signals here,
  // they get a soft chip below). Positive signals form the "why fired" story.
  const positiveSignals = (cluster.signals || []).filter(s => (s.contributed_score || 0) > 0);
  const negativeSignals = (cluster.signals || []).filter(s => (s.contributed_score || 0) < 0);

  return (
    <div>
      {/* Phase B-4 · Outcome banner — only shown once the cluster is disposed. */}
      {outcomeText && (
        <div
          data-testid="dup-outcome-banner"
          style={{
            padding: "10px 14px",
            background: "var(--surface-elev)",
            border: "1px solid var(--line-strong)",
            borderRadius: 6, marginBottom: 18, fontSize: 13,
          }}
        >
          <div style={{
            fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em",
            fontWeight: 600, color: "var(--ink-mute)", marginBottom: 4,
          }}>Outcome</div>
          <div>{outcomeText}</div>
          {cluster.resolved_at && (
            <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
              Recorded {fmt.dateTime(cluster.resolved_at)}{cluster.resolved_by ? ` by ${cluster.resolved_by}` : ""}.
            </div>
          )}
        </div>
      )}

      <Section label="Detection Summary">
        <KV k="Score" v={<span className="mono">{(cluster.score || 0).toFixed(2)}</span>} />
        <KV k="Confidence" v={(cluster.confidence_band || "").replace("_", " ")} />
        <KV k="Rule version" v={<span className="mono">{cluster.rule_version}</span>} />
        <KV k="Detected" v={fmt.dateTime(cluster.created_at)} />
        <KV k="Status" v={<StatusTag status={cluster.status} />} />
        {cluster.resolved_at && <KV k="Resolved" v={fmt.dateTime(cluster.resolved_at)} />}
        {cluster.resolved_by && <KV k="Resolved By" v={cluster.resolved_by} />}
      </Section>

      {/* Phase B-1 · Plain-English "Why this fired" bullets. */}
      <Section label="Why this was flagged">
        {positiveSignals.length === 0 && (
          <div className="muted" style={{ fontSize: 12 }}>No positive signals recorded.</div>
        )}
        <ul
          data-testid="dup-why-flagged"
          style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.5 }}
        >
          {positiveSignals.map((s, i) => (
            <li key={i} data-testid={`dup-why-${s.name}`}>{explainSignal(s)}</li>
          ))}
        </ul>
        {negativeSignals.length > 0 && (
          <div style={{ marginTop: 10, padding: "8px 10px",
                        background: "var(--blocker-soft)",
                        border: "1px solid var(--line)", borderRadius: 6,
                        fontSize: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Counter-evidence:</div>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {negativeSignals.map((s, i) => (
                <li key={i} data-testid={`dup-counter-${s.name}`}>{explainSignal(s)}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Keep raw chip list as a "details" line for power users. */}
        <details style={{ marginTop: 10 }}>
          <summary
            className="muted"
            style={{ fontSize: 11, cursor: "pointer", userSelect: "none" }}
            data-testid="dup-signals-raw-toggle"
          >
            Show raw signal scores
          </summary>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
            {(cluster.signals || []).map((s, i) => (
              <span key={i} data-testid={`dup-signal-${cluster.id}-${s.name}`} style={{
                fontSize: 11, padding: "3px 8px",
                background: s.contributed_score < 0 ? "var(--blocker-soft)" : "var(--surface-elev)",
                border: "1px solid var(--line)", borderRadius: 999,
              }}>
                <span style={{ fontWeight: 600 }}>{s.name}</span>{" "}
                <span className="mono" style={{ fontSize: 10 }}>
                  {s.contributed_score >= 0 ? "+" : ""}{s.contributed_score.toFixed(2)}
                </span>
              </span>
            ))}
          </div>
        </details>
      </Section>

      <Section label={`Transactions (${(cluster.transactions || []).length})`}>
        {(cluster.transactions || []).map(t => (
          <div key={t.id} data-testid={`dup-txn-${t.id}`} style={{
            padding: "10px 12px",
            border: "1px solid var(--line)", borderRadius: 6,
            marginBottom: 8,
            background: cluster.status === "confirmed_duplicate" && t.exclusion ? "var(--blocker-soft)" : "var(--surface)",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span className="mono" style={{ fontSize: 12, fontWeight: 600 }}>{t.transaction_date}</span>
              <span className="mono" style={{ fontSize: 13 }}>
                {t.dr_cr} ${fmt.currency(t.amount_unsigned)}
              </span>
            </div>
            <div style={{ fontSize: 13, marginTop: 4 }}>{t.description}</div>
            <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
              {t.reference && <>ref {t.reference} · </>}
              lifecycle {t.lifecycle_status}
              {t.exclusion && <> · excluded ({t.exclusion.type})</>}
            </div>
          </div>
        ))}
      </Section>

      {pending && (
        <Section label="Controller Disposition">
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10 }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12 }}>
              <span className="muted">Disposition</span>
              <select
                value={disposition} onChange={e => { setDisposition(e.target.value); setError(null); }}
                data-testid="dup-disposition-select"
                style={{ padding: 6, fontSize: 13, fontFamily: "inherit",
                         background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 4 }}
              >
                <option value="">— Select —</option>
                <option value="confirmed_duplicate">Confirmed duplicate (exclude all but one)</option>
                <option value="kept_both">Kept both (back to under review)</option>
                <option value="valid_repeat_activity">Valid repeat activity</option>
                <option value="false_positive">False positive (engine was wrong)</option>
              </select>
            </label>

            {/* Phase B-2 · Disposition help copy — shown once a disposition is picked. */}
            {disposition && DISPOSITION_HELP[disposition] && (
              <div
                data-testid={`dup-disposition-help-${disposition}`}
                style={{
                  padding: "8px 10px",
                  background: "var(--surface-elev)",
                  border: "1px solid var(--line)", borderRadius: 6,
                  fontSize: 12, lineHeight: 1.45,
                }}
              >
                <span style={{ fontWeight: 600, marginRight: 4 }}>What happens:</span>
                {DISPOSITION_HELP[disposition]}
              </div>
            )}

            {disposition === "confirmed_duplicate" && (
              <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12 }}>
                <span className="muted">Keep which transaction</span>
                <select
                  value={keptId} onChange={e => setKeptId(e.target.value)}
                  data-testid="dup-kept-select"
                  style={{ padding: 6, fontSize: 13, fontFamily: "inherit",
                           background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 4 }}
                >
                  <option value="">— Select —</option>
                  {(cluster.transactions || []).map(t => (
                    <option key={t.id} value={t.id}>
                      {t.transaction_date} · ${fmt.currency(t.amount_unsigned)} · {(t.description || "").slice(0, 40)}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12 }}>
              <span className="muted">Controller notes</span>
              <textarea
                value={notes} onChange={e => setNotes(e.target.value)}
                rows={3}
                data-testid="dup-notes-input"
                placeholder="Rationale (required for audit trail)"
                style={{ padding: 6, fontSize: 13, fontFamily: "inherit",
                         background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 4 }}
              />
            </label>
            {error && <div className="muted" style={{ color: "var(--blocker)", fontSize: 12 }} data-testid="dup-error">{error}</div>}
            <button
              className="btn primary btn-sm"
              onClick={dispose} disabled={busy}
              data-testid="dup-dispose-btn"
              style={{ width: "fit-content" }}
            >
              {busy ? "Recording…" : "Record Disposition"}
            </button>
          </div>
        </Section>
      )}
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div className="muted" style={{
        fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em",
        marginBottom: 8, fontWeight: 600,
      }}>{label}</div>
      {children}
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 8, padding: "4px 0", fontSize: 13 }}>
      <div className="muted">{k}</div>
      <div>{v}</div>
    </div>
  );
}
